import React, { useEffect, useRef } from "react";
import { Box, Typography } from "@mui/material";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { BRAND_BLUE, BRAND_GREEN } from "../../theme/colors";

const DEFAULT_CENTER = [42.6526, -73.7562];

const coordinatesFor = (property) => {
  if (property.latitude == null || property.latitude === "" || property.longitude == null || property.longitude === "") {
    return null;
  }
  const latitude = Number(property.latitude);
  const longitude = Number(property.longitude);
  return Number.isFinite(latitude) && Number.isFinite(longitude)
    ? [latitude, longitude]
    : null;
};

export default function PropertyPortfolioMap({ properties, onSelect }) {
  const nodeRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef(L.layerGroup());
  const onSelectRef = useRef(onSelect);

  useEffect(() => {
    onSelectRef.current = onSelect;
  }, [onSelect]);

  useEffect(() => {
    if (!nodeRef.current || mapRef.current) return undefined;

    const map = L.map(nodeRef.current, { scrollWheelZoom: false }).setView(DEFAULT_CENTER, 12);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "&copy; OpenStreetMap contributors",
      maxZoom: 19,
    }).addTo(map);
    markersRef.current.addTo(map);
    mapRef.current = map;

    const resizeTimer = window.setTimeout(() => map.invalidateSize(), 0);
    return () => {
      window.clearTimeout(resizeTimer);
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const markerLayer = markersRef.current;
    markerLayer.clearLayers();
    const bounds = [];

    properties.forEach((property) => {
      const coordinates = coordinatesFor(property);
      if (!coordinates) return;
      bounds.push(coordinates);

      const location = [property.streetAddress, property.city, property.state]
        .filter(Boolean)
        .join(", ");
      const marker = L.circleMarker(coordinates, {
        radius: 8,
        color: "#ffffff",
        fillColor: BRAND_GREEN,
        fillOpacity: 1,
        weight: 3,
      });
      const tooltip = document.createElement("div");
      const name = document.createElement("strong");
      name.textContent = property.name || "Unnamed property";
      tooltip.append(name);
      if (location) tooltip.append(document.createElement("br"), location);
      tooltip.append(document.createElement("br"), "Click to view property");
      marker.bindTooltip(tooltip, { direction: "top", offset: [0, -8], opacity: 0.96 });
      marker.on("click", () => onSelectRef.current?.(property));
      marker.addTo(markerLayer);
    });

    if (bounds.length === 1) map.setView(bounds[0], 15);
    else if (bounds.length > 1) map.fitBounds(bounds, { padding: [32, 32], maxZoom: 15 });
    else map.setView(DEFAULT_CENTER, 12);
  }, [properties]);

  const plottedCount = properties.filter(coordinatesFor).length;

  return (
    <Box>
      <Box sx={{ mb: 1.25, display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
        <Box>
          <Typography sx={{ color: BRAND_BLUE, fontWeight: 800 }}>Property map</Typography>
          <Typography variant="body2" color="text.secondary">Select a property marker to open its details.</Typography>
        </Box>
        <Typography variant="body2" sx={{ color: BRAND_BLUE, fontWeight: 700 }}>
          {plottedCount} of {properties.length} plotted
        </Typography>
      </Box>
      <Box
        ref={nodeRef}
        aria-label="Map of organization properties"
        sx={{ height: { xs: 320, md: 420 }, width: "100%", borderRadius: 1.5, overflow: "hidden", bgcolor: "grey.100", border: "1px solid #dfe5ec" }}
      />
    </Box>
  );
}
